import { Match, Switch, Show } from 'solid-js';
import { twJoin } from '@troon/tailwind-preset/merge';
import { Toast as KToast, toaster } from '@kobalte/core/toast';
import { IconInfo } from '@troon/icons/info';
import { IconCircleCheck } from '@troon/icons/circle-check';
import { IconCircleWarning } from '@troon/icons/circle-warning';
import { IconCloseMd } from '@troon/icons/close-md';
import { Button } from '../forms/button';
import type { JSX } from 'solid-js';

type Toast = {
	title: JSX.Element;
	description?: JSX.Element;
	variant?: 'negative' | 'positive' | 'info';
};

export function ToastRegion() {
	return (
		<KToast.Region pauseOnInteraction pauseOnPageIdle duration={5_000}>
			<KToast.List class="pointer-events-none fixed inset-4 z-50 flex flex-col items-center justify-end gap-4 md:inset-8 md:items-end" />
		</KToast.Region>
	);
}

export function addToast(options: Toast) {
	return toaster.show((props) => (
		<KToast
			toastId={props.toastId}
			class={twJoin(
				'pointer-events-auto flex w-full translate-x-0 animate-in flex-row items-start gap-2 rounded-lg px-2 py-1 shadow animation-duration-200 slide-in-from-bottom md:w-fit md:max-w-96 md:px-3 md:py-2 md:slide-in-from-bottom-0 md:slide-in-from-right ui-closed:animate-out ui-closed:slide-out-to-bottom md:ui-closed:slide-out-to-bottom-0 md:ui-closed:slide-out-to-right ui-swipe-move:translate-x-[var(--kb-toast-swipe-move-x)]',
				options?.variant === 'positive' && 'bg-green-100 text-green-500',
				options?.variant === 'negative' && 'bg-red-100 text-red-500',
				(options?.variant === 'info' || !options?.variant) && 'bg-brand-100 text-brand-500',
			)}
		>
			<Switch fallback={<IconInfo class="mt-2 size-8 shrink-0" title="Info" />}>
				<Match when={options?.variant === 'positive'}>
					<IconCircleCheck class="mt-2 size-8 shrink-0" title="Success" />
				</Match>
				<Match when={options?.variant === 'negative'}>
					<IconCircleWarning class="mt-2 size-8 shrink-0" title="Error" />
				</Match>
			</Switch>
			<div class="flex h-full grow flex-col gap-1">
				<KToast.Title class={twJoin('flex grow flex-col justify-center', options.description ? 'font-semibold' : '')}>
					{options.title}
				</KToast.Title>
				<Show when={options.description}>
					{(desc) => <KToast.Description class="text-sm">{desc()}</KToast.Description>}
				</Show>
			</div>
			<div class="shrink-0 grow-0">
				<KToast.CloseButton
					as={Button}
					appearance="transparent-current"
					action={options?.variant === 'negative' ? 'danger' : undefined}
				>
					<IconCloseMd />
				</KToast.CloseButton>
			</div>
		</KToast>
	));
}
